/**
 * Edit Display Name Modal
 * Allows users to update their display name
 */

import { useState, useEffect } from "react";
import { Modal } from "@components/admin/Modal";
import { useAuth } from "@contexts/AuthContext";

interface EditDisplayNameModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EditDisplayNameModal({
  isOpen,
  onClose,
}: EditDisplayNameModalProps) {
  const { user, updateProfile } = useAuth();
  const [displayName, setDisplayName] = useState(user?.full_name || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setDisplayName(user?.full_name || "");
      setError(null);
    }
  }, [isOpen, user?.full_name]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await updateProfile({ full_name: displayName.trim() || null });
      onClose();
    } catch (err) {
      const error = err as { detail?: string; message?: string };
      setError(
        error.detail || error.message || "Failed to update display name",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Display Name"
      description="Update your display name shown across the platform"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="edit-display-name-form"
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      <form id="edit-display-name-form" onSubmit={handleSubmit}>
        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="displayName"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Display Name
          </label>
          <input
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={255}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                     placeholder-gray-400 dark:placeholder-gray-500
                     focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Enter your display name"
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            This name will be displayed across the platform.
          </p>
        </div>
      </form>
    </Modal>
  );
}
